import { aesGcmDecrypt, aesGcmEncrypt, aesGcmOpen, aesGcmSeal } from '@/crypto/aes-gcm'
import {
  base64ToBytes,
  base64UrlToBytes,
  bytesToBase64,
  bytesToBase64Url,
  bytesToUtf8,
  utf8ToBytes,
} from '@/crypto/encoding'
import { randomBytes, randomIvBytes } from '@/crypto/random'
import type { SendCipherBlob, SendPayload } from '../types'

// ── パスワード保護プリミティブ (KEY_HIERARCHY.md §4.8.2) ─────────────────

/**
 * PBKDF2-HMAC-SHA256(password, passSalt, c=250_000, 32 byte) で passKey を導出する。
 *
 * @param password UTF-8 パスワード文字列
 * @param passSalt base64(16 byte random) または生の Uint8Array
 */
export const pbkdf2DerivePassKey = async (
  password: string,
  passSalt: Uint8Array,
): Promise<Uint8Array> => {
  const pwBytes = utf8ToBytes(password)

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    pwBytes.buffer.slice(
      pwBytes.byteOffset,
      pwBytes.byteOffset + pwBytes.byteLength,
    ) as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveBits'],
  )

  const saltBuffer = passSalt.buffer.slice(
    passSalt.byteOffset,
    passSalt.byteOffset + passSalt.byteLength,
  ) as ArrayBuffer

  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: saltBuffer, iterations: 250_000 },
    keyMaterial,
    256,
  )

  return new Uint8Array(bits)
}

/** パスワード保護用 passSalt (16 byte random) を生成して base64 で返す。 */
export const generatePassSalt = (): string => bytesToBase64(randomBytes(16))

/**
 * passKey で linkKey を AES-256-GCM Seal し { protectedSendKey, pskIv } を返す。
 * sealed = ciphertext || authTag (48 byte → base64 64 chars)
 */
export const sealSendKeyWithPassKey = async (
  passKey: Uint8Array,
  linkKey: Uint8Array,
): Promise<{ protectedSendKey: string; pskIv: string }> => {
  const iv = randomIvBytes()
  const sealed = await aesGcmSeal(passKey, iv, linkKey)

  return {
    protectedSendKey: bytesToBase64(sealed),
    pskIv: bytesToBase64(iv),
  }
}

/**
 * passKey で封印済み linkKey を開封して返す。
 * 鍵違いまたは改ざんは AES-GCM OperationError として throw される。
 */
export const unsealSendKeyWithPassKey = async (
  passKey: Uint8Array,
  protectedSendKey: string,
  pskIv: string,
): Promise<Uint8Array> => {
  const sealed = base64ToBytes(protectedSendKey)
  const iv = base64ToBytes(pskIv)

  return aesGcmOpen(passKey, iv, sealed)
}

/**
 * Send の暗号化プリミティブ (T-D-Send、ゼロ知識設計)。
 *
 * 鍵階層:
 *   linkKey (32 byte random) — コンテンツを AES-256-GCM 暗号化する鍵。
 *   sealLinkKey → SK で linkKey を wrap し { encryptedLinkKey, linkKeyIv } として保存。
 *   受信 URL フラグメント (#send/view/:id/:encodedKey) に base64url(linkKey) を埋め込む。
 *   サーバは linkKey を持たない (ゼロ知識)。
 */

/** 32 byte のランダム linkKey を生成する。 */
export const generateLinkKey = (): Uint8Array => randomBytes(32)

/** linkKey を URL フラグメント埋め込み用 base64url にエンコードする。 */
export const encodeLinkKey = (key: Uint8Array): string => bytesToBase64Url(key)

/** URL フラグメントから取得した base64url 文字列を linkKey (Uint8Array) に戻す。 */
export const decodeLinkKey = (encoded: string): Uint8Array => base64UrlToBytes(encoded)

/**
 * Send payload を linkKey で AES-256-GCM 暗号化する。
 * IV は毎回新規生成 (再利用禁止)。
 */
export const encryptSendPayload = async (
  linkKey: Uint8Array,
  payload: SendPayload,
): Promise<SendCipherBlob> => {
  const iv = randomIvBytes()
  const plaintext = utf8ToBytes(JSON.stringify(payload))

  return aesGcmEncrypt(linkKey, iv, plaintext) as Promise<SendCipherBlob>
}

const SEND_TYPES = ['text', 'file'] as const

const isSendPayload = (value: unknown): value is SendPayload => {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>

  if (!(SEND_TYPES as readonly string[]).includes(obj.type as string)) return false
  if (typeof obj.name !== 'string') return false

  return true
}

/**
 * SendCipherBlob を linkKey で復号し SendPayload を返す。
 *
 * @throws AES-GCM OperationError (auth tag 不一致 = 改ざん / 鍵違い)
 * @throws SyntaxError (JSON 破損)
 * @throws TypeError (復号後の JSON が SendPayload の形式に合わない)
 */
export const decryptSendPayload = async (
  linkKey: Uint8Array,
  cipher: SendCipherBlob,
): Promise<SendPayload> => {
  const plaintextBytes = await aesGcmDecrypt(linkKey, cipher)
  const parsed: unknown = JSON.parse(bytesToUtf8(plaintextBytes))

  if (!isSendPayload(parsed)) {
    throw new TypeError('decrypted payload is not a valid SendPayload')
  }

  return parsed
}

/**
 * SK で linkKey を wrap し、{ encryptedLinkKey: base64, linkKeyIv: base64 } を返す。
 * sealed = ciphertext || authTag (48 byte → 64 base64 chars、VARCHAR(96) に収まる)。
 */
export const sealLinkKey = async (
  symmetricKey: Uint8Array,
  linkKey: Uint8Array,
): Promise<{ encryptedLinkKey: string; linkKeyIv: string }> => {
  const iv = randomIvBytes()
  const sealed = await aesGcmSeal(symmetricKey, iv, linkKey)

  return {
    encryptedLinkKey: bytesToBase64(sealed),
    linkKeyIv: bytesToBase64(iv),
  }
}

/**
 * SK で封印済み linkKey を unwrap し、生の linkKey (Uint8Array) を返す。
 *
 * @throws AES-GCM OperationError (auth tag 不一致 = 改ざん / 鍵違い)
 */
export const unsealLinkKey = async (
  symmetricKey: Uint8Array,
  encryptedLinkKey: string,
  linkKeyIv: string,
): Promise<Uint8Array> => {
  const sealed = base64ToBytes(encryptedLinkKey)
  const iv = base64ToBytes(linkKeyIv)

  return aesGcmOpen(symmetricKey, iv, sealed)
}

// ── Send file 暗号化 (Phase 4 §4.3) ──────────────────────────────────

/**
 * Send file bytes を linkKey で AES-256-GCM 暗号化し、`iv || ciphertext || authTag` を
 * 1 本の base64 文字列にして返す (backend が期待する wire format)。
 *
 * IV は毎回新規生成 (再利用禁止)。返り値の byte 長 = plaintext + 28 (iv 12 + tag 16)。
 * backend `SendService.create` の長さ整合性チェック (size + 28) と一致する。
 *
 * 10 MB 入力 → 10 MB + 28 byte の Uint8Array → base64 化で ~14 MB。
 * UI 側は `File.arrayBuffer()` から Uint8Array を作って渡す。
 */
export const encryptSendFile = async (
  linkKey: Uint8Array,
  fileBytes: Uint8Array,
): Promise<{ fileCiphertext: string; size: number }> => {
  const iv = randomIvBytes()
  const sealed = await aesGcmSeal(linkKey, iv, fileBytes)
  const combined = new Uint8Array(iv.length + sealed.length)

  combined.set(iv, 0)
  combined.set(sealed, iv.length)

  return {
    fileCiphertext: bytesToBase64(combined),
    size: fileBytes.byteLength,
  }
}

/**
 * `encryptSendFile` の逆操作。受信エンドポイントから取得した bytes (`iv || ciphertext ||
 * authTag` の生バイナリ) を linkKey で復号して plaintext bytes を返す。
 *
 * 入力は ArrayBuffer (fetch response.arrayBuffer()) または Uint8Array のどちらも可。
 *
 * @throws AES-GCM OperationError (auth tag 不一致 = 改ざん / 鍵違い)
 * @throws RangeError (バイト長が短すぎる)
 */
export const decryptSendFile = async (
  linkKey: Uint8Array,
  bytes: Uint8Array | ArrayBuffer,
): Promise<Uint8Array> => {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  // iv(12) + at least 1 byte ciphertext + authTag(16) = 29 byte 最小
  if (u8.byteLength < 12 + 1 + 16) {
    throw new RangeError(`send file ciphertext too short: ${u8.byteLength} byte`)
  }
  const iv = u8.subarray(0, 12)
  const sealed = u8.subarray(12)

  return aesGcmOpen(linkKey, iv, sealed)
}
